import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { requireResponse } from "../core/errors/requireResponse";
import { londonDate } from "./format";
import { SILENT } from "./useDashboardData";

/**
 * Google Calendar for "What's on today" — the same endpoints and the same OAuth
 * hand-off as the Sales Rep tab's GoogleCalendarPrompt (which left Analytics
 * with Dashboard v2, so connecting has to work from here too):
 * GoogleCallbackPage writes `gcal-auth-state` + `gcal-auth-code` (or
 * `gcal-auth-error`) to localStorage, and whichever tab is listening POSTs
 * {code, state, redirect_uri} to /api/google-calendar/callback (state is
 * REQUIRED by the API since 2026-10-22).
 *
 * The card ALWAYS says where the calendar stands (Alex 2026-10-22: "how can
 * they connect, it is still not shown"): loading → a small skeleton; not
 * connected / status failed → Connect; connected → "Google Calendar · email ·
 * Disconnect"; connected but events failed (any status / code) → the reason +
 * Reconnect (unless reconnecting cannot help, e.g. the Calendar API is off).
 *
 * Keys are per USER, not per store: a calendar belongs to the person. Silent:
 * a store without Google configured must not toast on every landing.
 */

const REDIRECT = () => `${window.location.origin}/auth/google/callback`;
export const CODE_KEY = "gcal-auth-code";
export const STATE_KEY = "gcal-auth-state";
export const ERROR_KEY = "gcal-auth-error";
// Where GoogleCallbackPage sends the browser back when the consent screen ran
// in THIS tab (pop-up blocked): the Dashboard's My Day, which then finishes the
// exchange from the code in localStorage.
export const RETURN_KEY = "gcal-return-to";
export const RETURN_TO = "/dashboard?tab=day";

const take = (key) => {
  try {
    const v = window.localStorage.getItem(key);
    if (v) window.localStorage.removeItem(key);
    return v;
  } catch {
    return null;
  }
};

/** The pending hand-off from GoogleCallbackPage: `{ code, state, error }`, each removed once read. */
export const takeAuthResult = () => {
  const code = take(CODE_KEY);
  const state = take(STATE_KEY);
  const error = take(ERROR_KEY);
  return { code, state, error };
};

export const MSG = {
  notConfigured: "Google Calendar isn't set up for Retail Circle yet — ask an admin.",
  didNotComplete: "Google sign-in didn't complete — try again.",
  denied: "Google access was not granted.",
  apiDisabled: "Google Calendar API is not enabled for Retail Circle's Google project — ask an admin.",
  startFailed: "Couldn't start the Google sign-in. Try again in a moment.",
};

/** A connect-flow failure (auth-url / callback / the callback page's ?error=) as one sentence, or null. */
export const connectMessage = (err) => {
  if (typeof err === "string") return err === "access_denied" ? MSG.denied : MSG.didNotComplete;
  const code = err?.response?.data?.code;
  if (code === "GOOGLE_NOT_CONFIGURED") return MSG.notConfigured;
  if (code === "GOOGLE_BAD_REDIRECT" || code === "GOOGLE_BAD_STATE" || code === "GOOGLE_EXCHANGE_FAILED") return MSG.didNotComplete;
  return null;
};

/**
 * What the card says about a CONNECTED calendar whose events failed, or whose
 * grant lacks a calendar scope: `{ title, text, action: "reconnect"|"connect"|null }`, or null.
 */
export const calendarProblem = ({ connected, scopesOk, eventsError }) => {
  if (!connected) return null;
  if (eventsError) {
    const data = eventsError.response?.data || {};
    const code = data.code;
    if (code === "GOOGLE_RECONNECT_REQUIRED") return { title: "Your Google Calendar connection has expired", text: "Reconnect to see today's meetings here.", action: "reconnect" };
    if (code === "GOOGLE_NOT_CONNECTED") return { title: "Your Google Calendar is no longer connected", text: "Connect it again to see today's meetings here.", action: "connect" };
    if (code === "GOOGLE_REFUSED" && data.reason === "accessNotConfigured") return { title: "Couldn't read your Google Calendar", text: MSG.apiDisabled, action: null };
    const status = eventsError.response?.status;
    const why = data.reason || data.error || data.message || (status ? `error ${status}` : "no response");
    return { title: "Couldn't read your Google Calendar", text: `Reason: ${why}. Reconnecting usually fixes it.`, action: "reconnect" };
  }
  if (scopesOk === false) return { title: "Your Google Calendar needs permission again", text: "Reconnect and allow calendar access to see today's meetings here.", action: "reconnect" };
  return null;
};

export function useDashboardCalendar() {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const handled = useRef(false);

  const status = useQuery({
    queryKey: ["dashboard", "calendar-status"],
    queryFn: async () => requireResponse(await axios.get("/api/google-calendar/status", SILENT))?.data || { connected: false },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const s = status.data || {};
  const connected = !!s.connected;

  const events = useQuery({
    queryKey: ["dashboard", "calendar-events"],
    queryFn: async () => requireResponse(await axios.get("/api/google-calendar/events", SILENT))?.data?.events || [],
    staleTime: 2 * 60 * 1000,
    enabled: connected,
    retry: false,
  });

  const refreshAll = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["dashboard", "calendar-status"] });
    qc.invalidateQueries({ queryKey: ["dashboard", "calendar-events"] });
  }, [qc]);

  const exchange = useCallback(async ({ code, state, error }) => {
    if (error) {
      setConnectError(connectMessage(error));
      return;
    }
    if (!code || handled.current) return;
    handled.current = true;
    setConnecting(true);
    setConnectError(null);
    try {
      requireResponse(await axios.post("/api/google-calendar/callback", { code, state, redirect_uri: REDIRECT() }, SILENT));
      refreshAll();
    } catch (err) {
      setConnectError(connectMessage(err) || MSG.didNotComplete);
    } finally {
      setConnecting(false);
      handled.current = false;
    }
  }, [refreshAll]);

  useEffect(() => {
    exchange(takeAuthResult());
    const onStorage = (e) => {
      // The callback page writes the state first, then the code (or error): act on the last.
      if ((e.key === CODE_KEY || e.key === ERROR_KEY) && e.newValue) exchange(takeAuthResult());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [exchange]);

  const disconnectRequest = async () => requireResponse(await axios.delete("/api/google-calendar/disconnect", SILENT));

  // Open the tab synchronously, inside the click — a window.open() after an
  // await is a pop-up most browsers block (the old prompt's failure mode).
  const connect = async ({ disconnectFirst = false } = {}) => {
    setConnectError(null);
    let popup = null;
    try {
      popup = window.open("", "_blank");
    } catch {
      popup = null;
    }
    try {
      if (disconnectFirst) {
        try {
          await disconnectRequest();
        } catch {
          // A fresh consent overwrites the stored tokens anyway — carry on.
        }
      }
      const res = requireResponse(await axios.get("/api/google-calendar/auth-url", { params: { redirect_uri: REDIRECT() }, ...SILENT }));
      const url = res?.data?.url;
      if (!url) throw new Error("no url");
      if (popup && !popup.closed) {
        popup.location.href = url;
      } else {
        // Blocked: run the consent screen here and come back to My Day.
        try {
          window.localStorage.setItem(RETURN_KEY, RETURN_TO);
        } catch {
          // storage blocked — the callback page falls back to closing itself
        }
        window.location.assign(url);
      }
    } catch (err) {
      popup?.close?.();
      setConnectError(connectMessage(err) || MSG.startFailed);
    } finally {
      if (disconnectFirst) refreshAll();
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    setConnectError(null);
    try {
      await disconnectRequest();
      qc.setQueryData(["dashboard", "calendar-status"], (old) => ({ ...(old || {}), connected: false, email: null }));
      qc.removeQueries({ queryKey: ["dashboard", "calendar-events"] });
    } catch {
      setConnectError("Couldn't disconnect Google Calendar — try again.");
    } finally {
      setDisconnecting(false);
    }
  };

  const problem = calendarProblem({ connected, scopesOk: s.scopes_ok, eventsError: events.isError ? events.error : null });

  return {
    loading: status.isLoading,
    statusFailed: status.isError,
    connected,
    // configured:false (no Google client on this API) — say so instead of a Connect that can only fail.
    configured: s.configured !== false,
    email: s.email || null,
    connecting,
    disconnecting,
    events: connected && !events.isError ? events.data || [] : [],
    problem,
    reconnect: problem?.action === "reconnect",
    connectError,
    connect: () => connect(),
    reconnectNow: () => connect({ disconnectFirst: true }),
    disconnect,
  };
}

/**
 * "+ Add to-do" on What's on today: appends a to-do due TODAY to the Daily Recap
 * settings (the same `todos` list the Sales Rep to-do widget edits — same shape),
 * then refreshes My Day, whose schedule lists today's to-dos. Reads the list
 * fresh first so a to-do added elsewhere is never overwritten.
 */
export function useAddTodo() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const add = async (title) => {
    setBusy(true);
    setError(null);
    try {
      const current = requireResponse(await axios.get("/api/daily-recap/settings", SILENT))?.data || {};
      const today = londonDate();
      const todo = { id: Date.now(), title, description: null, startDate: today, endDate: today, done: false, syncToCalendar: false, createdAt: new Date().toISOString() };
      const todos = [...(Array.isArray(current.todos) ? current.todos : []), todo];
      const saved = requireResponse(await axios.put("/api/daily-recap/settings", { todos }, SILENT));
      qc.setQueriesData({ queryKey: ["daily-recap-settings"] }, (old) => (old ? { ...old, todos: saved?.data?.todos || todos } : old));
      qc.invalidateQueries({ queryKey: ["dashboard", "my-day"] });
      return true;
    } catch {
      setError("Couldn't add the to-do — try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { add, busy, error };
}
